import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, ExternalLink, X, Users2, PenTool, Target, Circle, Square, Share2, Pencil, Camera, ChevronDown, Settings2, Copy, Video, Send, PhoneOff, Link2, AlertTriangle } from "lucide-react";
import SnapshotModal from "@/components/whiteboard/SnapshotModal";
import LiveSubjectDrawer from "@/components/live/LiveSubjectDrawer";
import LiveSharedOverlay from "@/components/live/LiveSharedOverlay";
import ShareTargetModal from "@/components/live/ShareTargetModal";
import { ReactionsBar, ReactionsBurstLayer } from "@/components/live/LiveReactions";
import UnifiedWhiteboard from "@/pages/UnifiedWhiteboard";
import { openMeetTracked, autoEndClass } from "@/lib/meetWindow";

/* Two-tone chime for waiting-room knocks — pure Web Audio, no asset needed. */
function playKnockChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    [880, 1318].forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine"; o.frequency.value = f;
      o.connect(g); g.connect(ctx.destination);
      const t = ctx.currentTime + i * 0.18;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.35, t + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.6);
      o.start(t); o.stop(t + 0.65);
    });
    setTimeout(() => { try { ctx.close(); } catch { /* noop */ } }, 1600);
  } catch { /* audio blocked — popup still shows */ }
}

export default function LiveClassroom() {
  const { classId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [cls, setCls] = useState(null);
  const [loading, setLoading] = useState(true);
  const [panel, setPanel] = useState("none"); // none | attendance
  const [studentDirectory, setStudentDirectory] = useState({});
  const [attendance, setAttendance] = useState({}); // student_id -> status
  const [attendanceSource, setAttendanceSource] = useState({}); // student_id -> "auto" | "manual"
  // Engagement: time spent focused + tab-switches
  const [focusedSeconds, setFocusedSeconds] = useState(0);
  const [autoMarked, setAutoMarked] = useState(false);
  const [loadError, setLoadError] = useState(""); // "" | "not_found" | "forbidden" | "network"
  const [attentionAlerts, setAttentionAlerts] = useState([]);

  // ─────── Live shared overlay (subject toolkit) ───────
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeOverlay, setActiveOverlay] = useState(null);
  const [dismissedOverlayId, setDismissedOverlayId] = useState(""); // student locally closed a finished overlay
  const [spotlight, setSpotlight] = useState(null); // A16 — spotlit student
  const [reportOpen, setReportOpen] = useState(false); // teacher tech-issue report
  const wbOpenRef = useRef(false);
  const [wbOpen, setWbOpen] = useState(false); // Unified whiteboard drawer
  wbOpenRef.current = wbOpen;

  // NOTE: navigating away from this page does NOT dismiss the shared board —
  // the Google Meet call continues, so students keep the board until the
  // teacher explicitly closes it (X) or the class ends.

  // ANY path that opens the board (header button, toolkit, mobile menu…)
  // broadcasts it to students immediately — with one retry on failure.
  const isTeacherRef = useRef(false);
  useEffect(() => {
    if (!wbOpen || !isTeacherRef.current || !classId) return;
    let theme = "dark";
    try { theme = localStorage.getItem("wb.theme") || "dark"; } catch { /* noop */ }
    const broadcast = () => api.post(`/live/${classId}/share`, {
      type: "whiteboard", ref_id: classId, payload: { title: "Interactive Board", theme },
    });
    broadcast()
      .then(({ data }) => { if (data?.overlay) setActiveOverlay(data.overlay); })
      .catch(() => setTimeout(() => {
        broadcast().then(({ data }) => { if (data?.overlay) setActiveOverlay(data.overlay); }).catch(() => {});
      }, 1500));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wbOpen, classId]);

  const [wbCollab, setWbCollab] = useState(null); // { enabled, allowed_ids, allowed_names } — board draw permission
  const [wbCollabModal, setWbCollabModal] = useState(false); // teacher's "who can draw" picker
  const [boardLink, setBoardLink] = useState(""); // shareable live-board link for the Meet chat
  const openCollabModal = async () => {
    setWbCollabModal(true);
    if (!boardLink) {
      try {
        const { data } = await api.post(`/classes/${classId}/guest-token`);
        setBoardLink(`${window.location.origin}/guest/${classId}?token=${data.token}&direct=1`);
      } catch { /* link section simply hidden */ }
    }
  };
  const [wbFullscreen, setWbFullscreen] = useState(false);
  const [wbView, setWbView] = useState(null); // mirrored board viewport
  const [stripMenuOpen, setStripMenuOpen] = useState(false); // mobile: class controls collapsed behind one button
  const [studentSnapOpen, setStudentSnapOpen] = useState(false); // student "Share photo"
  const dismissedIdRef = useRef(""); // teacher just ended this overlay — ignore stale polls re-adding it

  // Poll live state every 2.5s — students and teacher both run this so the teacher
  // sees the overlay as soon as they share, and students get it as it broadcasts.
  useEffect(() => {
    if (!classId) return;
    let stop = false;
    let permErrShown = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/live/${classId}/state`);
        if (!stop) {
          const ov = data?.state?.active_overlay || null;
          setActiveOverlay(ov && ov.id === dismissedIdRef.current ? null : ov);
          setSpotlight(data?.state?.spotlight || null);
          setWbCollab(data?.state?.wb_collab || null);
          setWbView(data?.state?.wb_view || null);
        }
      } catch (e) {
        const st = e?.response?.status;
        if (!permErrShown && (st === 401 || st === 403 || st === 404)) {
          permErrShown = true;
          try { toast.error("You aren't enrolled in this class — quizzes & whiteboard from the teacher won't appear. Ask your teacher to add you or share the guest invite link."); } catch { /* noop */ }
        }
      }
    };
    tick();
    const t = setInterval(tick, 2500);
    return () => { stop = true; clearInterval(t); };
  }, [classId]);

  // Auto-open the Interactive Board on the student side ONLY when the teacher
  // grants Remote Access TO THIS STUDENT.
  useEffect(() => {
    const isT = user?.role === "teacher" && cls?.teacher_id === user?.id;
    if (isT) return;
    const boardShared = activeOverlay?.type === "whiteboard";
    const ids = wbCollab?.allowed_ids || [];
    const granted = !!wbCollab?.enabled && (ids.includes("*") || (!!user?.id && ids.includes(user.id)));
    if (boardShared) {
      setWbOpen(true);
    } else if (wbOpen && !boardShared) {
      setWbOpen(false);
    }
    void granted;
  }, [activeOverlay?.type, activeOverlay?.id, activeOverlay, wbCollab?.enabled, wbCollab?.allowed_ids, user?.role, user?.id, cls?.teacher_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissOverlay = async () => {
    try {
      await api.post(`/live/${classId}/dismiss`);
      setActiveOverlay(null);
      toast.success("Stopped sharing");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not stop share"); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/classes/${classId}`);
        setCls(data.class);
        // Load students directory + existing attendance (teacher only)
        if (user?.role === "teacher") {
          try {
            const s = await api.get("/students");
            const dir = {};
            (s.data.students || []).forEach((st) => { dir[st.id] = st.full_name; });
            setStudentDirectory(dir);
          } catch {}
          try {
            const att = await api.get(`/classes/${classId}/attendance`);
            const m = {};
            const src = {};
            (att.data.attendance || []).forEach((a) => { m[a.student_id] = a.status; src[a.student_id] = a.source || "manual"; });
            setAttendance(m);
            setAttendanceSource(src);
          } catch {}
        }
      } catch (e) {
        const status = e.response?.status;
        if (status === 404) setLoadError("not_found");
        else if (status === 403) setLoadError("forbidden");
        else setLoadError("network");
        toast.error(status === 404 ? "This class link is invalid or was deleted." : (e.response?.data?.detail || "Could not load class"));
      }
      finally { setLoading(false); }
    })();
  }, [classId, user?.role]);

  const isTeacherEarly = user?.role === "teacher" && cls?.teacher_id === user?.id;

  // Arrived via the sidebar "Interactive Board" with a live class → open the
  // board immediately (?board=1 set by BoardLauncher).
  useEffect(() => {
    if (cls && isTeacherEarly && searchParams.get("board") === "1") setWbOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cls?.id, isTeacherEarly]);

  // Teacher side — poll the waiting room (guests knocking via invite links)
  // and surface admit/decline controls. A chime plays for every NEW knock.
  const [waitingList, setWaitingList] = useState([]);
  const [waitingMinimized, setWaitingMinimized] = useState(false);
  const waitingIdsRef = useRef(new Set());
  useEffect(() => {
    if (!cls || !isTeacherEarly) return;
    const tick = async () => {
      try {
        const { data } = await api.get(`/live/${classId}/waiting`);
        const list = data.waiting || [];
        const prev = waitingIdsRef.current;
        if (list.some((w) => !prev.has(w.id))) { playKnockChime(); setWaitingMinimized(false); }
        waitingIdsRef.current = new Set(list.map((w) => w.id));
        setWaitingList(list);
      } catch { /* noop */ }
    };
    tick();
    const iv = setInterval(tick, 5000);
    return () => clearInterval(iv);
  }, [cls, isTeacherEarly, classId]);
  const resolveWaiting = async (entryId, action) => {
    try {
      await api.post(`/live/${classId}/waiting/${entryId}`, { action });
      setWaitingList((l) => l.filter((x) => x.id !== entryId));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Students report tab-switches so the teacher's attention alerts work.
  useEffect(() => {
    if (!cls || isTeacherEarly) return;
    const handler = () => {
      const away = document.visibilityState !== "visible";
      api.post(`/live/${classId}/attention-events`, { kind: away ? "tab_away" : "tab_back" }).catch(() => {});
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [cls, isTeacherEarly, classId]);

  // Presence heartbeat — students ping every 10s so the teacher's
  // share-target modal lists everyone who is actually online.
  useEffect(() => {
    if (!cls || isTeacherEarly || user?.role !== "student") return;
    const beat = () => api.post(`/live/${classId}/presence`).catch(() => {});
    beat();
    const t = setInterval(beat, 10000);
    return () => clearInterval(t);
  }, [cls, isTeacherEarly, classId, user?.role]);

  // Connection-quality telemetry — feeds the admin per-teacher activity
  // dashboard's internet-quality column.
  useEffect(() => {
    if (!cls) return;
    const send = () => {
      const c = navigator.connection || {};
      api.post(`/live/${classId}/net`, {
        rtt: typeof c.rtt === "number" ? c.rtt : null,
        downlink: typeof c.downlink === "number" ? c.downlink : null,
        effective_type: c.effectiveType || "",
      }).catch(() => {});
    };
    send();
    const t = setInterval(send, 45000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cls?.id, classId]);

  // Teacher polls attention alerts every 3s and pops a red toast for each
  // NEW `tab_away` from a student.
  const attnSinceRef = useRef("");
  useEffect(() => {
    if (!cls || !isTeacherEarly) return;
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/live/${classId}/attention-events`, { params: attnSinceRef.current ? { since: attnSinceRef.current } : {} });
        if (stop) return;
        const events = data?.events || [];
        if (!events.length) return;
        attnSinceRef.current = events[events.length - 1].created_at;
        setAttentionAlerts((prev) => [...prev, ...events].slice(-40));
        for (const ev of events) {
          if (ev.kind === "tab_away") {
            toast.error(`👀 ${ev.student_name} switched away from the class`, { duration: 5000 });
          }
        }
      } catch { /* keep trying */ }
    };
    tick();
    const t = setInterval(tick, 3000);
    return () => { stop = true; clearInterval(t); };
  }, [cls, isTeacherEarly, classId]);

  // Heartbeat: count focused seconds + auto-attendance ping at 5 min for students.
  useEffect(() => {
    if (!cls) return;
    const tick = setInterval(() => {
      if (document.visibilityState === "visible") setFocusedSeconds((s) => s + 1);
    }, 1000);
    return () => clearInterval(tick);
  }, [cls, user?.role]);

  // On join, students are immediately marked "quit" (auto) — it upgrades to
  // "present" once they cross the 5-minute mark below.
  const joinMarkedRef = useRef(false);
  useEffect(() => {
    if (!cls || user?.role !== "student" || joinMarkedRef.current) return;
    joinMarkedRef.current = true;
    api.post(`/classes/${classId}/auto-attend`, { duration_seconds: 0 }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cls?.id, user?.role]);

  // Browser recording refs (see startRecording further down).
  const mediaRecRef = useRef(null);
  const recStreamRef = useRef(null);
  const recMimeRef = useRef("video/webm");
  const recStartRef = useRef(0);
  const stopRecordingRef = useRef(null);
  const recQueueRef = useRef([]); // chunks waiting to stream to the server
  const recPumpingRef = useRef(false);
  const recIndexRef = useRef(0);
  const recUploadIdRef = useRef("");
  const [recPromptOpen, setRecPromptOpen] = useState(false);
  const [recording, setRecording] = useState(false);

  // Leaving the page with the recorder still running → stop & save it.
  useEffect(() => () => {
    try { stopRecordingRef.current?.(); } catch { /* noop */ }
  }, []);

  // Tab/window closed mid-recording → fire a keepalive finalize.
  useEffect(() => {
    const onPageHide = () => {
      if (mediaRecRef.current?.state !== "recording" || !recUploadIdRef.current) return;
      try {
        const token = localStorage.getItem("ts_token");
        fetch(`${API}/classes/${classId}/recording/complete`, {
          method: "POST", keepalive: true,
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ upload_id: recUploadIdRef.current, mime: recMimeRef.current || "video/webm" }),
        });
      } catch { /* noop */ }
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId]);

  // Teacher: live-refresh attendance every 15s so auto marks show up without
  // a reload. Rows the teacher touched locally are never overwritten.
  const manualTouchedRef = useRef(new Set());
  useEffect(() => {
    if (!cls || user?.role !== "teacher") return;
    const t = setInterval(async () => {
      try {
        const att = await api.get(`/classes/${classId}/attendance`);
        const rows = att.data.attendance || [];
        setAttendance((prev) => {
          const m = { ...prev };
          rows.forEach((a) => { if (!manualTouchedRef.current.has(a.student_id)) m[a.student_id] = a.status; });
          return m;
        });
        setAttendanceSource((prev) => {
          const s = { ...prev };
          rows.forEach((a) => { if (!manualTouchedRef.current.has(a.student_id)) s[a.student_id] = a.source || "manual"; });
          return s;
        });
      } catch { /* ignore */ }
    }, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cls?.id, user?.role, classId]);

  useEffect(() => {
    if (!cls || autoMarked) return;
    if (user?.role !== "student") return;
    if (focusedSeconds < 300) return;
    (async () => {
      try {
        const { data } = await api.post(`/classes/${classId}/auto-attend`, { duration_seconds: focusedSeconds });
        if (data?.ok) {
          setAutoMarked(true);
          toast.success("Attendance auto-marked: Present");
        }
      } catch { /* silent */ }
    })();
  }, [focusedSeconds, cls, user?.role, autoMarked, classId]);

  // Fire teacher-joined notification once per class open (backend debounces to 1/hour).
  const notifiedRef = useRef(false);
  useEffect(() => {
    if (!cls || !isTeacherEarly || !classId || notifiedRef.current) return;
    notifiedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post(`/classes/${classId}/notify-teacher-joined`);
        if (!cancelled && data?.notified > 0) {
          toast.success(`Notified ${data.notified} student${data.notified === 1 ? "" : "s"} — you're live`);
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [cls, classId, isTeacherEarly]);

  // Every 60 minutes of continuous recording, ask the teacher whether to carry on.
  useEffect(() => {
    if (!recording) { setRecPromptOpen(false); return; }
    const t = setInterval(() => {
      if (recStartRef.current && Date.now() - recStartRef.current >= 60 * 60 * 1000) {
        setRecPromptOpen(true);
      }
    }, 30000);
    return () => clearInterval(t);
  }, [recording]);

  // Auto-stop the recording when the class completes — covers every way the
  // Google Meet call can end (teacher closed the Meet tab from another page,
  // clicked "Leave call" inside Meet, lost connection…). The backend sweep
  // flips the class to completed; we watch for it while recording.
  useEffect(() => {
    if (!recording || !isTeacherEarly) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/classes/${classId}`);
        if (data?.class?.status === "completed") {
          toast.info("Google Meet call ended — stopping and saving the recording");
          stopRecordingRef.current?.();
        }
      } catch { /* transient network — try again next tick */ }
    }, 15000);
    return () => clearInterval(t);
  }, [recording, isTeacherEarly, classId]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading class…</div>;
  if (!cls) {
    const isNotFound = loadError === "not_found";
    const isForbidden = loadError === "forbidden";
    const dest = user?.role === "student" ? "/student/classes" : "/teacher/classes";
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0C10] text-white px-4">
        <div className="max-w-md w-full bg-[#141821] border border-white/10 rounded-lg p-6 text-center" data-testid="live-load-error">
          <div className="mx-auto w-12 h-12 rounded-full bg-red-500/15 border border-red-500/40 flex items-center justify-center mb-3">
            <X className="text-red-400" size={22} />
          </div>
          <h2 className="font-display font-bold text-xl mb-1">
            {isNotFound ? "This class link is invalid" : isForbidden ? "You don't have access" : "Couldn't load this class"}
          </h2>
          <p className="text-sm text-neutral-400 mb-5">
            {isNotFound && "The class was moved or deleted. Ask your teacher for a fresh link."}
            {isForbidden && "You aren't a member of this class. Ask your teacher to add you."}
            {!isNotFound && !isForbidden && "Network hiccup — try again in a moment."}
          </p>
          <div className="flex flex-col gap-2">
            <Link to={dest} data-testid="live-load-error-back" className="text-sm px-4 py-2 rounded-md bg-white text-[#0A0C10] font-semibold hover:bg-neutral-100">
              ← Back to my classes
            </Link>
            {!isNotFound && !isForbidden && (
              <button onClick={() => window.location.reload()} data-testid="live-load-error-retry" className="text-xs text-neutral-400 hover:text-white">
                Retry
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const isTeacher = isTeacherEarly;
  isTeacherRef.current = isTeacher;
  const meetUrl = cls.meet_url || cls.meeting_link || "";

  const openMeet = () => {
    if (!meetUrl) return toast.error("No Google Meet link on this class yet");
    if (isTeacher) {
      // Reuses the already-open Meet tab (no duplicate join screens); closing
      // it ends the class only when the call has truly finished.
      openMeetTracked(meetUrl, () => autoEndClass(classId, {
        onDone: () => {
          try { stopRecordingRef.current?.(); } catch { /* noop */ }
          nav("/teacher/classes");
        },
      }), classId);
    } else {
      openMeetTracked(meetUrl, null, classId);
    }
  };
  const copyMeetLink = async () => {
    if (!meetUrl) return toast.error("No Google Meet link on this class yet");
    try { await navigator.clipboard.writeText(meetUrl); toast.success("Google Meet link copied"); }
    catch { toast.error("Copy failed — long-press to copy manually"); }
  };
  const generateMeet = async () => {
    try {
      const { data } = await api.post(`/classes/${classId}/generate-meet`);
      setCls((c) => ({ ...c, meet_url: data.meet_url, meeting_platform: "google_meet" }));
      toast.success("Google Meet link created");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create the Meet link"); }
  };
  const sendMeetToStudents = async () => {
    try {
      const { data } = await api.post(`/classes/${classId}/send-meet-link`, {});
      toast.success(`Sent — ${data.notified} notified, ${data.emailed} emailed (Meet + live board link included)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send the link"); }
  };
  const endClass = async () => {
    if (!window.confirm("End this class for everyone?")) return;
    try { stopRecordingRef.current?.(); } catch { /* noop */ }
    try { await api.post(`/classes/${classId}/complete`); } catch { /* noop */ }
    toast.success("Class ended");
    nav("/teacher/classes");
  };
  const leaveClass = () => {
    nav(user?.role === "student" ? "/student/classes" : "/teacher/classes");
  };

  // ── Browser-native session recording — STREAMED (unchanged pipeline). ──
  const pumpRecQueue = async () => {
    if (recPumpingRef.current) return;
    recPumpingRef.current = true;
    try {
      while (recQueueRef.current.length) {
        const item = recQueueRef.current[0];
        let ok = false;
        for (let a = 0; a < 3 && !ok; a++) {
          try {
            const fd = new FormData();
            fd.append("upload_id", recUploadIdRef.current);
            fd.append("index", String(item.index));
            fd.append("mime", recMimeRef.current || "video/webm");
            fd.append("file", item.blob, `part-${item.index}`);
            await api.post(`/classes/${classId}/recording/chunk`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            ok = true;
          } catch { await new Promise((r) => setTimeout(r, 1200 * (a + 1))); }
        }
        if (!ok) break;
        recQueueRef.current.shift();
      }
    } finally { recPumpingRef.current = false; }
  };
  const finalizeRecording = async () => {
    try {
      recStreamRef.current?.display?.getTracks().forEach((t) => t.stop());
      recStreamRef.current?.mic?.getTracks().forEach((t) => t.stop());
    } catch { /* noop */ }
    recStreamRef.current = null;
    if (!recIndexRef.current) { toast.error("Recording was empty — nothing to save"); return; }
    toast.loading("Finalizing recording…", { id: "rec-upload" });
    const t0 = Date.now();
    while (recQueueRef.current.length && Date.now() - t0 < 180000) {
      await pumpRecQueue();
      if (recQueueRef.current.length) await new Promise((r) => setTimeout(r, 800));
    }
    try {
      const { data } = await api.post(`/classes/${classId}/recording/complete`, {
        upload_id: recUploadIdRef.current, total_chunks: recIndexRef.current, mime: recMimeRef.current || "video/webm",
      });
      toast.success(
        data?.already_saved
          ? "Recording already saved to your dashboard"
          : data?.drive_queued
            ? "Recording saved to your dashboard — Google Drive copy uploading in background"
            : "Recording saved to your dashboard (Recordings page)",
        { id: "rec-upload", duration: 8000 },
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Recording save failed", { id: "rec-upload" });
    }
  };
  const startRecording = async () => {
    if (recording) return;
    let display = null;
    let mic = null;
    try {
      try {
        display = await navigator.mediaDevices.getDisplayMedia({
          video: { frameRate: { ideal: 10, max: 12 }, width: { max: 1280 }, height: { max: 720 } },
          audio: true,
          preferCurrentTab: false,
          selfBrowserSurface: "include",
          surfaceSwitching: "include",
        });
      } catch (err) {
        if (err?.name === "NotAllowedError" || err?.name === "AbortError") throw err;
        display = await navigator.mediaDevices.getDisplayMedia({
          video: { frameRate: { ideal: 10, max: 12 }, width: { max: 1280 }, height: { max: 720 } },
        });
      }
      try { mic = await navigator.mediaDevices.getUserMedia({ audio: true }); } catch { /* mic optional */ }
      const tracks = [display.getVideoTracks()[0]];
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC && (display.getAudioTracks().length || mic)) {
        const actx = new AC();
        const dest = actx.createMediaStreamDestination();
        if (display.getAudioTracks().length) actx.createMediaStreamSource(new MediaStream([display.getAudioTracks()[0]])).connect(dest);
        if (mic) actx.createMediaStreamSource(mic).connect(dest);
        if (dest.stream.getAudioTracks().length) tracks.push(dest.stream.getAudioTracks()[0]);
      } else if (mic && mic.getAudioTracks().length) {
        tracks.push(mic.getAudioTracks()[0]);
      }
      const stream = new MediaStream(tracks);
      recStreamRef.current = { display, mic };
      const mimeCandidates = [
        'video/mp4;codecs="avc1.42E01E,mp4a.40.2"',
        "video/mp4",
        "video/webm;codecs=vp8,opus",
        "video/webm",
      ];
      const mime = mimeCandidates.find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "video/webm";
      recMimeRef.current = mime.split(";")[0];
      let mr;
      try {
        mr = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 500000, audioBitsPerSecond: 48000 });
      } catch {
        recMimeRef.current = "video/webm";
        mr = new MediaRecorder(stream, { videoBitsPerSecond: 500000, audioBitsPerSecond: 48000 });
      }
      recUploadIdRef.current = `rec${Date.now()}`;
      recIndexRef.current = 0;
      recQueueRef.current = [];
      mr.ondataavailable = (e) => {
        if (!e.data?.size) return;
        recQueueRef.current.push({ index: recIndexRef.current++, blob: e.data });
        pumpRecQueue();
      };
      mr.onstop = () => finalizeRecording();
      display.getVideoTracks()[0].addEventListener("ended", () => {
        try { if (mr.state !== "inactive") mr.stop(); } catch { /* noop */ }
        setRecording(false);
      });
      mr.start(15000); // 15s chunks — streamed to the server as the class runs
      mediaRecRef.current = mr;
      recStartRef.current = Date.now();
      setRecording(true);
      toast.success("Recording started — pick the Google Meet tab (with 'share tab audio') to capture the class. It saves to the server continuously.");
    } catch (e) {
      try { display?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
      try { mic?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
      recStreamRef.current = null;
      if (e?.name === "NotAllowedError" || e?.name === "AbortError") {
        toast.error("Recording cancelled — choose a screen or tab in the popup and press “Share”.", { duration: 9000 });
      } else if (!navigator.mediaDevices?.getDisplayMedia) {
        toast.error("This browser can't record the screen — please use Chrome or Edge on a desktop/laptop.", { duration: 8000 });
      } else {
        toast.error(`Recording failed (${e?.name || "error"}): ${e?.message || "unknown"} — try Chrome/Edge on desktop.`, { duration: 9000 });
      }
    }
  };
  const stopRecording = () => {
    try { if (mediaRecRef.current && mediaRecRef.current.state !== "inactive") mediaRecRef.current.stop(); } catch { /* noop */ }
    if (!mediaRecRef.current || mediaRecRef.current.state === "inactive") {
      try {
        recStreamRef.current?.display?.getTracks().forEach((t) => t.stop());
        recStreamRef.current?.mic?.getTracks().forEach((t) => t.stop());
      } catch { /* noop */ }
      recStreamRef.current = null;
    }
    setRecording(false);
    setRecPromptOpen(false);
    recStartRef.current = 0;
  };
  stopRecordingRef.current = stopRecording;
  const toggleRecording = () => (recording ? stopRecording() : startRecording());

  // Copy the shareable guest invite link.
  const copyInviteLink = async () => {
    try {
      const { data } = await api.post(`/classes/${classId}/guest-token`);
      const link = `${window.location.origin}/guest/${classId}?token=${data.token}`;
      try { await navigator.clipboard.writeText(link); } catch { /* fall through */ }
      toast.success("Invite link copied — share it with your students");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate invite link");
    }
  };

  const setAttFor = (sid, status) => {
    manualTouchedRef.current.add(sid);
    setAttendance((prev) => ({ ...prev, [sid]: status }));
    setAttendanceSource((prev) => ({ ...prev, [sid]: "manual" }));
  };

  const saveAttendance = async () => {
    const entries = (cls.student_ids || []).map((sid) => ({ student_id: sid, status: attendance[sid] || "absent" }));
    try {
      await api.post(`/classes/${classId}/attendance`, { entries, visible_to_student: true });
      toast.success(`Saved attendance for ${entries.length} student${entries.length !== 1 ? "s" : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save attendance"); }
  };

  return (
    <div className="h-[100dvh] max-h-[100dvh] bg-[#0A0C10] text-white flex flex-col overflow-hidden">
      {/* Top bar */}
      <div className="min-h-14 flex items-center flex-wrap gap-2 px-3 sm:px-4 py-2 border-b border-white/10 bg-[#10141C]">
        {/* ── Waiting room — teacher popup for guest knocks: modal + chime */}
        {isTeacher && waitingList.length > 0 && !waitingMinimized && (
          <div className="fixed inset-0 z-[97] bg-black/55 backdrop-blur-[2px] flex items-center justify-center p-4" data-testid="waiting-room-panel">
            <div className="w-full max-w-lg bg-[#0B1220]/98 border-2 border-[#22D3EE]/70 rounded-2xl p-6 sm:p-7 shadow-[0_0_90px_rgba(34,211,238,0.55)] animate-in fade-in zoom-in-95">
              <div className="flex items-center gap-2.5 mb-4">
                <span className="relative flex h-3.5 w-3.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#22D3EE] opacity-75" />
                  <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-[#22D3EE]" />
                </span>
                <div className="font-display font-bold text-white text-xl sm:text-2xl tracking-tight">
                  {waitingList.length === 1 ? "Someone is waiting to join" : `${waitingList.length} people waiting to join`}
                </div>
              </div>
              <div className="space-y-3 max-h-[50vh] overflow-y-auto">
                {waitingList.map((w) => (
                  <div key={w.id} className="flex items-center gap-3.5 bg-white/5 border border-white/10 rounded-xl px-4 py-3.5" data-testid={`waiting-entry-${w.id}`}>
                    <div className="w-11 h-11 rounded-full bg-[#22D3EE]/15 border border-[#22D3EE]/40 inline-flex items-center justify-center text-[#22D3EE] font-bold text-base shrink-0">
                      {(w.name || "?").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-base font-bold text-white truncate">{w.name}</div>
                      <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-400">{w.role} · waiting</div>
                    </div>
                    <button onClick={() => resolveWaiting(w.id, "admit")} data-testid={`admit-${w.id}`}
                      className="text-sm font-bold px-6 py-3 rounded-full bg-[#22D3EE] text-[#05070C] hover:shadow-[0_0_22px_rgba(34,211,238,0.8)] hover:scale-105 transition-all">Admit</button>
                    <button onClick={() => resolveWaiting(w.id, "deny")} data-testid={`deny-${w.id}`}
                      className="text-sm font-bold px-4 py-3 rounded-full border border-red-400/60 text-red-300 hover:bg-red-500/20 transition-colors">Decline</button>
                  </div>
                ))}
              </div>
              <button
                data-testid="waiting-handle-later"
                onClick={() => setWaitingMinimized(true)}
                className="mt-4 w-full text-xs font-mono uppercase tracking-[0.18em] text-neutral-400 hover:text-white border border-white/10 hover:border-white/30 rounded-lg py-2 transition-colors"
              >
                Handle later — keep teaching
              </button>
            </div>
          </div>
        )}
        <Link to={user?.role === "student" ? "/student/classes" : "/teacher/classes"} className="text-sm inline-flex items-center gap-2 text-neutral-400 hover:text-white">
          <ArrowLeft size={16} /> <span className="hidden sm:inline">Back</span>
        </Link>
        <div className="ml-2 sm:ml-6 min-w-0 flex-shrink">
          <div className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 truncate">LIVE · {cls.subject}</div>
          <div className="font-display font-bold text-sm sm:text-base truncate max-w-[160px] sm:max-w-none inline-flex items-center gap-1.5">
            {cls.title}
            {isTeacher && (
              <button
                data-testid="rename-class-btn"
                title="Rename this class"
                onClick={async () => {
                  const t = window.prompt("Rename class", cls.title);
                  if (!t || !t.trim() || t.trim() === cls.title) return;
                  try {
                    await api.patch(`/classes/${classId}`, { title: t.trim() });
                    setCls((c) => ({ ...c, title: t.trim() }));
                    toast.success("Class renamed");
                  } catch (e) { toast.error(e.response?.data?.detail || "Rename failed"); }
                }}
                className="text-neutral-500 hover:text-[#22D3EE] transition-colors shrink-0"
              >
                <Pencil size={12} />
              </button>
            )}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-1.5 sm:gap-2 flex-wrap">
          {/* Student — capture & share a photo of their work with the tutor */}
          {!isTeacher && (
            <button
              onClick={() => setStudentSnapOpen(true)}
              data-testid="student-share-photo-btn"
              title="Take a picture of your work and share it with your teacher"
              className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.18em] px-2 sm:px-3 py-1.5 rounded-full border border-[#22D3EE]/40 bg-[#22D3EE]/10 text-[#22D3EE] hover:bg-[#22D3EE]/25 transition-all"
            >
              <Camera size={12} /> <span className="hidden sm:inline">Share photo</span>
            </button>
          )}
          {isTeacher && (
            <button
              onClick={() => setReportOpen(true)}
              data-testid="report-problem-btn"
              title="Report a technical problem in this class"
              className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.18em] px-2 sm:px-3 py-1.5 rounded-full border border-amber-400/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/25 transition-all"
            >
              <AlertTriangle size={12} /> <span className="hidden sm:inline">Report a problem</span>
            </button>
          )}
          {isTeacher && (
            <button
              onClick={() => setWbOpen(true)}
              data-testid="open-whiteboard"
              className="text-xs sm:text-sm font-semibold px-3 sm:px-5 py-2 sm:py-2.5 rounded-full border border-[#6366F1]/50 bg-[#6366F1]/10 text-[#A5B4FC] hover:text-white hover:bg-[#6366F1]/30 hover:shadow-[0_0_18px_rgba(99,102,241,0.55)] hover:scale-105 inline-flex items-center gap-1.5 sm:gap-2 transition-all duration-200"
            >
              <PenTool size={15} /> <span className="hidden sm:inline">Interactive Board</span><span className="sm:hidden">Board</span>
            </button>
          )}
          {!isTeacher && (
            <button
              onClick={leaveClass}
              data-testid="leave-class-btn"
              title="Leave this class"
              className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-full border border-red-400/50 bg-red-500/15 text-red-300 hover:bg-red-500 hover:text-white transition-colors"
            >
              <PhoneOff size={13} /> <span className="hidden sm:inline">Leave</span>
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
        {/* ── Google Meet hub — the video call itself lives in a new tab ── */}
        <div className="flex-1 relative overflow-y-auto" data-testid="video-area">
          {/* A16 · Spotlight banner (visible to everyone in the class) */}
          {spotlight?.student_name && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 bg-[#FFD600] text-[#0A0C10] rounded-md shadow-lg px-4 py-2 inline-flex items-center gap-2 font-semibold text-sm animate-fade-up" data-testid="spotlight-banner">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              Spotlight · {spotlight.student_name}
              {isTeacher && (
                <button
                  onClick={async () => {
                    try { await api.post(`/live/${classId}/spotlight/clear`); setSpotlight(null); toast.success("Spotlight cleared"); }
                    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                  }}
                  data-testid="spotlight-clear-btn"
                  className="ml-2 text-[10px] font-mono uppercase tracking-wider border border-[#0A0C10]/30 hover:border-[#0A0C10] px-2 py-0.5 rounded"
                >Clear ×</button>
              )}
            </div>
          )}

          <div className="min-h-full flex items-center justify-center p-6" data-testid="meet-hub">
            <div className="w-full max-w-2xl bg-[#0B1220] border border-[#00A48F]/30 rounded-2xl p-6 sm:p-10 shadow-[0_0_60px_rgba(0,164,143,0.12)]">
              <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.25em] text-[#2DD4BF]">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                </span>
                / live class · google meet
              </div>
              <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mt-3">{cls.title}</h2>
              <p className="text-sm text-neutral-400 mt-1">{cls.subject}{cls.batch_name ? ` · ${cls.batch_name}` : ""}</p>

              {meetUrl ? (
                <>
                  <button
                    onClick={openMeet}
                    data-testid="join-meet-btn"
                    className="mt-6 w-full inline-flex items-center justify-center gap-2.5 px-6 py-4 rounded-xl bg-[#00897B] hover:bg-[#00A48F] text-white font-bold text-base sm:text-lg transition-all hover:shadow-[0_0_30px_rgba(0,164,143,0.5)] hover:scale-[1.01]"
                  >
                    <Video size={20} /> Join Google Meet <ExternalLink size={15} />
                  </button>
                  <div className="mt-3 flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5">
                    <Link2 size={13} className="text-neutral-500 shrink-0" />
                    <span className="text-xs font-mono text-neutral-300 truncate flex-1" data-testid="meet-url-display">{meetUrl}</span>
                    <button onClick={copyMeetLink} data-testid="copy-meet-link"
                      className="text-[11px] font-bold px-2.5 py-1.5 rounded-md bg-white/10 hover:bg-white/25 text-white shrink-0 inline-flex items-center gap-1 transition-colors">
                      <Copy size={11} /> Copy
                    </button>
                  </div>
                  {isTeacher && (
                    <div className="mt-3 grid sm:grid-cols-2 gap-2">
                      <button onClick={sendMeetToStudents} data-testid="send-meet-to-students"
                        className="inline-flex items-center justify-center gap-2 text-sm font-bold rounded-lg py-2.5 bg-[#FFD600] text-[#0A0C10] hover:brightness-105 transition-all">
                        <Send size={14} /> Send link to students
                      </button>
                      <button onClick={copyInviteLink} data-testid="hub-copy-invite"
                        className="inline-flex items-center justify-center gap-2 text-sm font-semibold rounded-lg py-2.5 border border-white/15 text-neutral-200 hover:border-[#FFD600]/60 hover:text-[#FFD600] transition-all">
                        <Share2 size={14} /> Copy guest invite link
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="mt-6 bg-white/5 border border-dashed border-white/15 rounded-xl p-6 text-center" data-testid="no-meet-link">
                  <div className="text-sm text-neutral-300 font-semibold">No Google Meet link on this class yet.</div>
                  {isTeacher ? (
                    <>
                      <p className="text-xs text-neutral-500 mt-1.5">Generate one now — it takes a second and is reused for every session of this class.</p>
                      <button onClick={generateMeet} data-testid="generate-meet-btn"
                        className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#00897B] hover:bg-[#00A48F] text-white text-sm font-bold transition-colors">
                        <Video size={14} /> Generate Google Meet link
                      </button>
                    </>
                  ) : (
                    <p className="text-xs text-neutral-500 mt-1.5">Your teacher hasn't attached a video link yet — the board and activities below still work.</p>
                  )}
                </div>
              )}

              <p className="text-[11px] text-neutral-500 mt-5 leading-relaxed">
                The video call opens in a <strong className="text-neutral-300">new tab</strong>. Keep this tab open — the Interactive Board, quizzes, games, slides and attendance all happen right here.
              </p>
              {isTeacher && (
                <p className="text-[11px] text-neutral-500 mt-2 leading-relaxed" data-testid="meet-record-hint">
                  <strong className="text-[#2DD4BF]">Want to record?</strong> Open the <strong className="text-[#A5B4FC]">Interactive Board</strong> and hit the red <strong className="text-red-300">Record</strong> button in its top bar — in the popup pick the <strong className="text-neutral-300">Google Meet tab</strong> and tick <em>"Also share tab audio"</em>. The session saves straight to your Recordings page. Closing the Google Meet tab ends the class, the recording and every shared screen automatically.
                </p>
              )}
              {attentionAlerts.filter((a) => a.kind === "tab_away").length > 0 && isTeacher && (
                <div className="mt-3 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-md border bg-red-500/20 text-red-300 border-red-400/40" data-testid="attention-alerts">
                  👀 {attentionAlerts.filter((a) => a.kind === "tab_away").length} tab-away events
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right sidebar: Attendance (teacher). */}
        {(panel === "attendance" && isTeacher) && (
        <aside data-testid="attendance-drawer" className="fixed inset-y-0 right-0 z-[75] w-full sm:w-96 bg-[#10141C] border-l border-[#22D3EE]/25 flex flex-col shadow-[0_0_60px_rgba(0,0,0,0.6)]">
            <div className="p-4 flex-1 overflow-y-auto" data-testid="attendance-panel">
              <div className="flex items-center justify-between mb-3">
                <div className="font-display font-bold inline-flex items-center gap-2"><Users2 size={16} className="text-[#FFD600]" /> Attendance</div>
                <button onClick={() => setPanel("none")}><X size={16} /></button>
              </div>
              {(cls.student_ids || []).length === 0 ? (
                <div className="text-sm text-neutral-400">No students assigned to this class.</div>
              ) : (
                <>
                  <div className="space-y-2">
                    {(cls.student_ids || []).map((sid) => {
                      const isAuto = attendanceSource[sid] === "auto";
                      const isSpot = spotlight?.student_id === sid;
                      const studentName = studentDirectory[sid] || sid.slice(0, 8);
                      return (
                        <div
                          key={sid}
                          className={`p-3 border transition-colors ${isSpot
                            ? "bg-[#FFD600]/15 border-[#FFB400]"
                            : isAuto
                              ? "bg-emerald-500/[0.08] border-emerald-500/50"
                              : "bg-[#0A0C10] border-white/10"}`}
                          data-testid={`att-row-${sid}`}
                          data-auto-marked={isAuto ? "true" : "false"}
                          data-spotlit={isSpot ? "true" : "false"}
                        >
                          <div className="flex items-center justify-between mb-2 gap-2">
                            <div className="text-sm font-medium truncate">{studentName}</div>
                            <div className="flex items-center gap-1">
                              {isAuto && !isSpot && (
                                <span data-testid={`att-auto-${sid}`} className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-emerald-500/25 text-emerald-200 border border-emerald-400/40">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />Auto
                                </span>
                              )}
                              {isSpot && (
                                <span data-testid={`att-spotlight-${sid}`} className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-[#FFD600] text-[#0A0C10] font-bold">
                                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />Spotlight
                                </span>
                              )}
                              <button
                                onClick={async () => {
                                  try {
                                    if (isSpot) { await api.post(`/live/${classId}/spotlight/clear`); setSpotlight(null); }
                                    else {
                                      const { data } = await api.post(`/live/${classId}/spotlight`, { student_id: sid, student_name: studentName, reason: "manual" });
                                      setSpotlight(data.spotlight);
                                    }
                                  } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                                }}
                                data-testid={`spotlight-toggle-${sid}`}
                                title={isSpot ? "Clear spotlight" : "Spotlight this student"}
                                className={`inline-flex items-center justify-center w-6 h-6 rounded transition-colors ${isSpot ? "bg-[#FFD600] text-[#0A0C10]" : "border border-white/15 text-neutral-300 hover:border-[#FFD600] hover:text-[#FFD600]"}`}
                              ><Target size={11} /></button>
                            </div>
                          </div>
                          <div className="grid grid-cols-5 gap-1">
                            {["present", "late", "absent", "excused", "quit"].map((s) => (
                              <button
                                key={s}
                                data-testid={`att-${sid}-${s}`}
                                onClick={() => setAttFor(sid, s)}
                                className={`text-[10px] py-1.5 uppercase tracking-wider font-semibold ${attendance[sid] === s
                                  ? (s === "present" ? "bg-green-500 text-black" : s === "absent" ? "bg-red-500 text-white" : s === "late" ? "bg-amber-400 text-black" : s === "quit" ? "bg-orange-500 text-black" : "bg-neutral-300 text-black")
                                  : "border border-white/15 text-neutral-300 hover:border-white/40"}`}
                              >{s === "quit" ? "quit" : s.slice(0, 3)}</button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <button data-testid="att-save" onClick={saveAttendance} className="mt-4 w-full bg-[#FFD600] text-[#0A0C10] font-bold py-2.5">
                    Save attendance
                  </button>
                  <p className="text-[11px] text-neutral-500 mt-2">Visible to student & parent portal.</p>
                </>
              )}
            </div>
        </aside>
        )}
      </div>

      {/* Teacher's subject toolkit drawer */}
      {isTeacher && (
        <LiveSubjectDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          classId={classId}
          onShared={(overlay) => setActiveOverlay(overlay)}
        />
      )}

      {/* Student photo share — camera capture + AI check, lands in the teacher's snapshot feed */}
      {!isTeacher && studentSnapOpen && (
        <SnapshotModal open={studentSnapOpen} onClose={() => setStudentSnapOpen(false)} classId={classId} isTeacher={false} />
      )}

      {/* Active live overlay — shown to BOTH teacher and students. */}
      {activeOverlay?.type !== "whiteboard" && activeOverlay?.id !== dismissedOverlayId && (
        <LiveSharedOverlay
          overlay={activeOverlay}
          classId={classId}
          isTeacher={isTeacher}
          onDismiss={dismissOverlay}
          myParticipantId={user?.id}
          myName={user?.full_name}
          onStudentClose={!isTeacher && activeOverlay ? () => setDismissedOverlayId(activeOverlay.id) : undefined}
        />
      )}

      {/* Floating reactions UI — visible to everyone */}
      <ReactionsBurstLayer classId={classId} />
      <ReactionsBar classId={classId} />

      {/* Unified Whiteboard — full-area panel; video happens in the Meet tab. */}
      {wbOpen && (
        <div className={`${wbFullscreen ? "fixed inset-0 z-[60]" : "fixed inset-0 top-14 lg:absolute lg:left-0 lg:right-0 lg:top-0 lg:bottom-0 z-[55] lg:z-20"} bg-white flex flex-col`} data-testid="wb-inline-panel">
          {isTeacher ? (
            <div data-testid="board-live-strip" className="shrink-0 flex items-center flex-nowrap overflow-x-auto no-scrollbar gap-1.5 sm:gap-2 px-2 sm:px-3 py-2 bg-[#0B1220] border-b border-[#22D3EE]/25 [&>*]:shrink-0 [&>*]:whitespace-nowrap">
              <button
                data-testid="board-back-btn"
                onClick={() => {
                  setWbOpen(false); setWbFullscreen(false);
                  if (activeOverlay?.id) dismissedIdRef.current = activeOverlay.id;
                  setActiveOverlay(null);
                  api.post(`/live/${classId}/dismiss`).catch(() => {});
                  nav("/teacher");
                }}
                title="Back to your dashboard"
                className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-md bg-white/10 border border-white/25 text-white hover:bg-white hover:text-[#0A0C10] transition-colors"
              >
                <ArrowLeft size={14} /> Back
              </button>
              <div className="hidden sm:block h-4 w-px bg-white/15" />
              <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-[#22D3EE] max-w-[180px] truncate" data-testid="strip-live-label">
                <span className="relative flex h-2 w-2 shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                </span>
                Live · {cls.title}
              </span>
              <div className="hidden sm:block h-4 w-px bg-white/15" />
              {/* Mobile: collapse the class tools behind one button */}
              <button
                onClick={() => setStripMenuOpen((v) => !v)}
                data-testid="strip-mobile-toggle"
                className={`lg:hidden inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-md border transition-colors ${stripMenuOpen ? "bg-[#22D3EE] text-[#05070C] border-transparent" : "border-[#22D3EE]/40 bg-[#22D3EE]/10 text-[#22D3EE]"}`}
              >
                <Settings2 size={13} /> Controls
                <ChevronDown size={12} className={`transition-transform ${stripMenuOpen ? "rotate-180" : ""}`} />
              </button>
              <div className={`${stripMenuOpen ? "flex items-center gap-1.5" : "hidden"} lg:contents`}>
              <button
                data-testid="subject-toolkit-btn"
                onClick={() => setDrawerOpen(true)}
                title="Subject Toolkit — quizzes, games, stories, slides & AI helpers"
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border border-[#FFD600]/50 bg-[#FFD600]/10 text-[#FFD600] hover:bg-[#FFD600] hover:text-[#0A0C10] transition-all"
              >
                <Sparkles size={13} /> Subject Toolkit{activeOverlay && activeOverlay.type !== "whiteboard" ? " · sharing" : ""}
              </button>
              <button
                data-testid="attendance-toggle"
                onClick={() => setPanel(panel === "attendance" ? "none" : "attendance")}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border transition-all ${panel === "attendance"
                  ? "bg-[#22D3EE] text-[#05070C] border-transparent"
                  : "border-[#22D3EE]/40 bg-[#22D3EE]/10 text-[#22D3EE] hover:bg-[#22D3EE]/25"}`}
              >
                <Users2 size={13} /> Attendance
              </button>
              <button
                data-testid="record-toggle"
                onClick={toggleRecording}
                title={recording ? "Stop recording" : "Record this session"}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border transition-all ${recording
                  ? "bg-red-500/20 border-red-400/60 text-red-200 shadow-[0_0_14px_rgba(239,68,68,0.5)]"
                  : "border-red-400/40 bg-red-500/10 text-red-300 hover:bg-red-500/25 hover:text-white"}`}
              >
                {recording ? (
                  <><span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /><Square size={11} className="fill-red-300 text-red-300" /> Stop recording</>
                ) : (
                  <><Circle size={12} className="fill-red-500 text-red-500" /> Record</>
                )}
              </button>
              <button
                data-testid="copy-invite-link"
                onClick={copyInviteLink}
                title="Copy shareable guest link (no sign-up needed)"
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border border-white/15 bg-white/5 text-neutral-200 hover:border-[#FFD600]/60 hover:text-[#FFD600] transition-all"
              >
                <Share2 size={13} /> Invite
              </button>
              {waitingList.length > 0 && (
                <button
                  data-testid="strip-waiting-badge"
                  onClick={() => setWaitingMinimized(false)}
                  title="Students are waiting to join — click to admit or decline"
                  className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-md border border-[#22D3EE]/60 bg-[#22D3EE]/15 text-[#22D3EE] hover:bg-[#22D3EE]/30 hover:shadow-[0_0_14px_rgba(34,211,238,0.5)] transition-all"
                >
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#22D3EE] opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#22D3EE]" />
                  </span>
                  {waitingList.length} waiting
                </button>
              )}
              {attentionAlerts.filter((a) => a.kind === "tab_away").length > 0 && (
                <span
                  data-testid="strip-attention-alerts"
                  title={`${attentionAlerts.filter((a) => a.kind === "tab_away").length} tab-away events this session`}
                  className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-md border bg-red-500/20 text-red-300 border-red-400/40"
                >
                  👀 {attentionAlerts.filter((a) => a.kind === "tab_away").length} away
                </span>
              )}
              </div>
              <div className="flex-1" />
              <button
                onClick={() => setWbFullscreen((v) => !v)}
                data-testid="wb-fullscreen-toggle"
                className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-md border border-white/15 text-neutral-300 hover:border-white/50 hover:text-white transition-colors"
                title={wbFullscreen ? "Shrink board back into the class layout" : "Expand board to full screen"}
              >
                {wbFullscreen ? "⇲ Shrink" : "⇱ Expand"}
              </button>
            </div>
          ) : (
            /* ── Student status strip — slim, view-only info ── */
            <div data-testid="student-board-strip" className="shrink-0 flex items-center flex-nowrap overflow-x-auto no-scrollbar gap-2 px-2 sm:px-3 py-1.5 bg-[#0B1220] border-b border-[#22D3EE]/25 [&>*]:shrink-0 [&>*]:whitespace-nowrap">
              <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-[#22D3EE] max-w-[200px] truncate" data-testid="student-strip-live-label">
                <span className="relative flex h-2 w-2 shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                </span>
                Live · {cls.title}
              </span>
              {(() => {
                const ids = wbCollab?.allowed_ids || [];
                const canDraw = !!wbCollab?.enabled && (ids.includes("*") || (!!user?.id && ids.includes(user.id)));
                return canDraw ? (
                  <span data-testid="student-strip-candraw" className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border border-emerald-400/40 bg-emerald-500/10 text-emerald-300">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> You can draw
                  </span>
                ) : (
                  <span data-testid="student-strip-viewonly" className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border border-amber-400/40 bg-amber-500/10 text-amber-300">
                    👁 View only
                  </span>
                );
              })()}
              <div className="flex-1" />
              <button
                onClick={() => setWbFullscreen((v) => !v)}
                data-testid="wb-fullscreen-toggle"
                className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider px-3 py-1 rounded-md border border-white/15 text-neutral-300 hover:border-white/50 hover:text-white transition-colors"
                title={wbFullscreen ? "Shrink board back into the class layout" : "Expand board to full screen"}
              >
                {wbFullscreen ? "⇲ Shrink" : "⇱ Expand"}
              </button>
            </div>
          )}
          <UnifiedWhiteboard
            embedded
            classId={classId}
            subjectId={cls?.subject_id || ""}
            isTeacher={isTeacher}
            defaultCollab={true}
            readOnly={!isTeacher && !(wbCollab?.enabled && ((wbCollab?.allowed_ids || []).includes("*") || (wbCollab?.allowed_ids || []).includes(user?.id)))}
            forcedTheme={!isTeacher ? (activeOverlay?.payload?.theme || null) : null}
            followView={wbView}
            onViewBroadcast={(isTeacher || (wbCollab?.enabled && ((wbCollab?.allowed_ids || []).includes("*") || (wbCollab?.allowed_ids || []).includes(user?.id))))
              ? (v) => api.post(`/live/${classId}/wb-view`, v).catch(() => {})
              : undefined}
            onThemeChange={isTeacher ? (t) => api.post(`/live/${classId}/overlay-update`, { payload: { theme: t } }).catch(() => {}) : undefined}
            collabPermission={isTeacher ? (wbCollab || { enabled: false }) : undefined}
            onCollabControl={isTeacher ? openCollabModal : undefined}
            onClose={() => {
              setWbOpen(false); setWbFullscreen(false);
              if (isTeacher) {
                if (activeOverlay?.id) dismissedIdRef.current = activeOverlay.id;
                setActiveOverlay(null);
                api.post(`/live/${classId}/dismiss`).catch(() => {});
              }
            }}
          />
        </div>
      )}
      {/* Board collab permission picker */}
      {isTeacher && (
        <ShareTargetModal
          open={wbCollabModal}
          classId={classId}
          boardLink={boardLink}
          title="Board Remote Access"
          subtitle="Turning on Remote Access lets the chosen students draw and move things on the Interactive Board. You can change this anytime."
          confirmLabel="Give remote access"
          allowDisable={!!wbCollab?.enabled}
          onDisable={async () => {
            try {
              await api.post(`/live/${classId}/wb-permissions`, { enabled: false, allowed_ids: [], allowed_names: [] });
              setWbCollab({ enabled: false, allowed_ids: [], allowed_names: [] });
              setWbCollabModal(false);
              toast.success("Collab turned off — students are viewers now");
            } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
          }}
          onClose={() => setWbCollabModal(false)}
          onConfirm={async (ids, names) => {
            try {
              await api.post(`/live/${classId}/wb-permissions`, { enabled: true, allowed_ids: ids, allowed_names: names });
              setWbCollab({ enabled: true, allowed_ids: ids, allowed_names: names });
              setWbCollabModal(false);
              toast.success(ids.includes("*") ? "Everyone can now draw on the board" : `${names.length} participant${names.length === 1 ? "" : "s"} can now draw`);
            } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
          }}
        />
      )}

      {/* 60-minute recording check-in — stops runaway recordings from filling Drive */}
      {recPromptOpen && recording && (
        <div className="fixed inset-0 z-[97] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="rec-continue-modal">
          <div className="bg-[#10141C] border border-white/15 rounded-xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-1.5">
              <span className="w-10 h-10 rounded-full bg-red-500/15 border border-red-400/40 flex items-center justify-center">
                <Circle size={16} className="fill-red-500 text-red-500 animate-pulse" />
              </span>
              <div className="font-display font-bold text-lg text-white">Still recording — carry on?</div>
            </div>
            <p className="text-sm text-neutral-400 leading-relaxed">
              It's been 60 minutes of continuous recording. Do you wish to keep going, or stop and save the video now?
            </p>
            <div className="mt-5 flex flex-col-reverse sm:flex-row gap-2">
              <button
                data-testid="rec-continue-stop"
                onClick={() => { setRecPromptOpen(false); stopRecording(); }}
                className="flex-1 text-sm px-4 py-2.5 rounded-md border border-white/15 text-neutral-300 hover:border-white/40 hover:text-white"
              >
                Stop &amp; save
              </button>
              <button
                data-testid="rec-continue-keep"
                onClick={() => { recStartRef.current = Date.now(); setRecPromptOpen(false); }}
                className="flex-1 text-sm px-4 py-2.5 rounded-md bg-red-500 text-white font-semibold hover:bg-red-600"
              >
                Keep recording
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Teacher — report a technical problem (feeds Pilot KPI + emails admin) */}
      {reportOpen && isTeacher && (
        <ReportProblemModal classId={classId} onClose={() => setReportOpen(false)} />
      )}
    </div>
  );
}

const REPORT_CATEGORIES = [
  { k: "audio", label: "Audio problem" },
  { k: "video", label: "Video problem" },
  { k: "board", label: "Interactive Board" },
  { k: "meet_connection", label: "Meet connection" },
  { k: "screen_share", label: "Screen sharing" },
  { k: "other", label: "Other" },
];

function ReportProblemModal({ classId, onClose }) {
  const [category, setCategory] = useState("");
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const submit = async () => {
    if (!category) return toast.error("Pick what went wrong first");
    setSending(true);
    try {
      await api.post("/kpi/tech-issues", { class_id: classId, category, comment: comment.trim() });
      toast.success("Problem reported — the support team has been notified");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the report");
    }
    setSending(false);
  };
  return (
    <div className="fixed inset-0 z-[97] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="report-problem-modal">
      <div className="bg-[#10141C] border border-white/15 rounded-xl p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-1.5">
          <span className="w-10 h-10 rounded-full bg-amber-500/15 border border-amber-400/40 flex items-center justify-center">
            <AlertTriangle size={16} className="text-amber-300" />
          </span>
          <div className="font-display font-bold text-lg text-white">Report a problem</div>
          <button onClick={onClose} className="ml-auto p-1.5 text-neutral-400 hover:text-white" data-testid="report-problem-close"><X size={16} /></button>
        </div>
        <p className="text-sm text-neutral-400 leading-relaxed">What went wrong in this class? Your report goes straight to the support team.</p>
        <div className="mt-4 grid grid-cols-2 gap-2">
          {REPORT_CATEGORIES.map((c) => (
            <button
              key={c.k}
              onClick={() => setCategory(c.k)}
              data-testid={`report-cat-${c.k}`}
              className={`text-xs font-semibold px-3 py-2.5 rounded-lg border transition-colors text-left ${category === c.k
                ? "border-amber-400 bg-amber-500/20 text-amber-200"
                : "border-white/15 text-neutral-300 hover:border-white/40"}`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Optional — describe what happened…"
          rows={3}
          maxLength={1000}
          data-testid="report-comment-input"
          className="mt-3 w-full bg-[#0A0C10] border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-neutral-500 outline-none focus:border-amber-400/60 resize-none"
        />
        <button
          onClick={submit}
          disabled={sending || !category}
          data-testid="report-problem-submit"
          className="mt-3 w-full py-2.5 rounded-lg bg-amber-400 text-[#0A0C10] font-bold text-sm hover:brightness-105 disabled:opacity-40 transition-all"
        >
          {sending ? "Sending…" : "Send report"}
        </button>
      </div>
    </div>
  );
}
